import React from 'react';
import { useAccessibleClick } from '../../../hooks/use-accessible-click';
import { cn } from '../../../utils/cn';

interface SearchSuggestionItemProps {
    label: string;
    type?: string;
    query: string;
    onClick: () => void;
    variant?: 'default' | 'header';
    menuColor?: 'light' | 'dark';
    hasIntent?: boolean;
}

export const SearchSuggestionItem: React.FC<SearchSuggestionItemProps> = (
    props,
) => {
    const {
        onClick,
        label,
        type,
        query,
        variant = 'default',
        menuColor = 'dark',
    } = props;
    const { onClick: handleClick, onKeyDown } = useAccessibleClick(onClick);

    return (
        <div
            onClick={handleClick}
            onKeyDown={onKeyDown}
            className={cn(
                'focus:outline-hidden group flex cursor-pointer items-center',
                variant === 'header' && menuColor === 'light'
                    ? 'md:focus:bg-white/10'
                    : variant === 'header' && menuColor === 'dark'
                      ? 'md:focus:bg-gray-200'
                      : '',
            )}
            role="option"
            tabIndex={0}
            aria-selected={false}
        >
            <div
                className={cn(
                    'flex min-h-4.5 max-w-full items-center gap-1 whitespace-nowrap border-b border-transparent text-dropdown leading-[1.1] md:min-h-0',
                    variant === 'header' && menuColor === 'light'
                        ? 'text-gray-100 group-hover:border-white'
                        : variant === 'header' && menuColor === 'dark'
                          ? 'text-gray-600 group-hover:border-gray-600'
                          : 'text-[#9b9b9b] group-hover:border-gray-400 md:text-gray-400',
                )}
            >
                <span className="flex-1 truncate">
                    {highlightMatch(label, query, variant, menuColor)}
                </span>
                <span
                    className={cn(
                        'truncate md:text-dropdown',
                        variant === 'header' && menuColor === 'light'
                            ? 'text-white'
                            : 'text-gray-800',
                    )}
                >
                    {type ? ` in ${type}` : ''}
                </span>
            </div>
        </div>
    );
};

const highlightMatch = (
    text: string,
    query: string,
    variant: 'default' | 'header' = 'default',
    menuColor: 'light' | 'dark' = 'dark',
) => {
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    const unmatchedClass =
        variant === 'header' && menuColor === 'light'
            ? 'py-0.5 text-dropdown text-gray-200'
            : variant === 'header' && menuColor === 'dark'
              ? 'py-0.5 text-dropdown text-gray-600'
              : 'py-0.5 text-dropdown text-gray-400';

    const matchedClass =
        variant === 'header' && menuColor === 'light'
            ? 'py-0.5 text-dropdown text-white'
            : 'py-0.5 text-dropdown text-gray-800';

    if (index !== -1) {
        const before = text.slice(0, index);
        const match = text.slice(index, index + query.length);
        const after = text.slice(index + query.length);
        return (
            <>
                <span className={unmatchedClass}>{before}</span>
                <span className={matchedClass}>{match}</span>
                <span className={unmatchedClass}>{after}</span>
            </>
        );
    }
    return <span className={unmatchedClass}>{text}</span>;
};
